import type { ContractorPaymentResultV1 } from "@impetusos/contracts"
import type { ReactElement } from "react"

export interface ContractorPaymentResultPanelProps {
  status: "loading" | "ready" | "empty" | "error" | "denied"
  result?: ContractorPaymentResultV1
  contractorLabel?: string
  onRetry: () => void
  onDownload1099: (accessEventId: string) => void
}

const USD_FORMATTER = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
})

const money = (amount: string): string => USD_FORMATTER.format(Number(amount))

export function ContractorPaymentResultPanel({
  status,
  result,
  contractorLabel = "Contractor",
  onRetry,
  onDownload1099,
}: ContractorPaymentResultPanelProps): ReactElement {
  if (status === "loading") {
    return <p aria-live="polite">Loading contractor payment result...</p>
  }
  if (status === "denied") {
    return <p role="alert">You do not have access to this contractor result.</p>
  }
  if (status === "error") {
    return (
      <div role="alert">
        <p>Contractor payment results are temporarily unavailable.</p>
        <button type="button" onClick={onRetry}>Try again</button>
      </div>
    )
  }
  if (status === "empty" || !result) {
    return <p>No finalized contractor payment result is available.</p>
  }

  const settlementLabel = {
    processing: "Processing",
    settled: "Settled",
    failed: "Failed",
  }[result.state]
  const approvedPayable = result.payableKind === "contractor_hourly"
    ? `${result.approvedMinutes ?? 0} approved minutes`
    : money(result.approvedAmount ?? "0.00")

  return (
    <section aria-labelledby="contractor-payment-result-title">
      <header>
        <p>Contractor payment - Powered by Worklio</p>
        <h2 id="contractor-payment-result-title">
          {contractorLabel} payment result
        </h2>
        <p>
          {result.payPeriodStart} through {result.payPeriodEnd}
        </p>
      </header>
      <dl>
        <div><dt>Approved payable</dt><dd>{approvedPayable}</dd></div>
        <div>
          <dt>Payable type</dt>
          <dd>
            {result.payableKind === "contractor_hourly" ? "Hourly" : "Job"}
          </dd>
        </div>
        <div>
          <dt>Worklio gross payment</dt>
          <dd>{money(result.providerGrossAmount)}</dd>
        </div>
        <div>
          <dt>Worklio net payment</dt>
          <dd>{money(result.providerNetAmount)}</dd>
        </div>
        <div><dt>Settlement</dt><dd>{settlementLabel}</dd></div>
        <div>
          <dt>1099 status</dt>
          <dd>
            {result.form1099Status === "available"
              ? `${result.form1099Year} form available`
              : `${result.form1099Year} form not yet available`}
          </dd>
        </div>
      </dl>
      <p>
        Payment and settlement values are supplied by Worklio and are not
        recalculated by ImpetusOS. Contractor results remain separate from
        employee payroll.
      </p>
      {result.form1099Status === "available" ? (
        <button
          type="button"
          onClick={() => onDownload1099(result.accessEventId)}
          aria-label={`Download ${result.form1099Year} 1099 for ${contractorLabel}`}
        >
          Download 1099
        </button>
      ) : (
        <p role="status">1099 form not yet available.</p>
      )}
    </section>
  )
}
